import React, { useEffect, useMemo, useState } from 'react';
import { Shield, RefreshCw, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Read-only paginated feed of every privileged action recorded by
 * services.audit_log.record_audit(). Admin-only — non-admins receive 403
 * from the backend and the panel shows a forbidden message.
 *
 * Filters: action, actor email (substring), since-days.
 */
const AdminAuditLog = () => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filterAction, setFilterAction] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [sinceDays, setSinceDays] = useState('');
  const [knownActions, setKnownActions] = useState([]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const headers = () => {
    const t = localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (filterAction) params.set('action', filterAction);
      if (filterActor) params.set('actor_email', filterActor);
      if (sinceDays) params.set('since_days', sinceDays);
      const res = await fetch(`${API_URL}/api/admin/audit/events?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || `HTTP ${res.status}`);
        setEvents([]);
        setTotal(0);
      } else {
        setEvents(data.events || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadKnownActions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/audit/actions`, { headers: headers() });
      const data = await res.json();
      if (res.ok) setKnownActions(data.actions || []);
    } catch (_e) {
      /* non-fatal */
    }
  };

  useEffect(() => { loadKnownActions(); }, []); // eslint-disable-line
  useEffect(() => { load(); }, [page]); // eslint-disable-line

  const submitFilters = (e) => {
    e?.preventDefault?.();
    if (page !== 1) setPage(1);
    else load();
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch (_e) { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="admin-audit-log-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-700" />
          <h2 className="text-lg font-semibold text-gray-900">Admin Audit Log</h2>
          <span className="text-xs text-gray-500" data-testid="audit-total-count">({total} events)</span>
        </div>
        <button
          onClick={() => (page === 1 ? load() : setPage(1))}
          disabled={loading}
          data-testid="audit-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <form
        onSubmit={submitFilters}
        className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-4 gap-2"
        data-testid="audit-filters"
      >
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Action</span>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 border rounded-md text-sm bg-white"
            data-testid="audit-filter-action"
          >
            <option value="">All actions</option>
            {knownActions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Actor email contains</span>
          <input
            type="text"
            value={filterActor}
            onChange={(e) => setFilterActor(e.target.value)}
            placeholder="e.g. obed"
            data-testid="audit-filter-actor"
            className="mt-1 w-full px-2 py-1.5 border rounded-md text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Since (days)</span>
          <input
            type="number"
            min="1"
            max="365"
            value={sinceDays}
            onChange={(e) => setSinceDays(e.target.value)}
            placeholder="any"
            data-testid="audit-filter-since"
            className="mt-1 w-full px-2 py-1.5 border rounded-md text-sm"
          />
        </label>
        <div className="flex items-end">
          <button
            type="submit"
            data-testid="audit-filter-apply"
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md inline-flex items-center gap-1.5 w-full sm:w-auto justify-center"
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700" data-testid="audit-error">
          {error}
        </div>
      )}

      <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
        <table className="min-w-full text-sm" data-testid="audit-events-table">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Actor</th>
              <th className="px-3 py-2">Target</th>
              <th className="px-3 py-2">IP</th>
              <th className="px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {events.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No events match the current filters.</td></tr>
            )}
            {events.map((ev) => (
              <tr key={ev.id} className="hover:bg-indigo-50/30">
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmt(ev.at)}</td>
                <td className="px-3 py-2 font-mono text-xs text-indigo-700">{ev.action}</td>
                <td className="px-3 py-2 text-gray-800">{ev.actor_email || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-gray-600">
                  {ev.target_type ? <span className="text-xs text-gray-500">{ev.target_type}: </span> : null}
                  <span className="font-mono text-xs break-all">{ev.target_id || '—'}</span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-gray-500">{ev.ip || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-600 max-w-md">
                  {ev.details && Object.keys(ev.details).length > 0 ? (
                    <details>
                      <summary className="cursor-pointer text-indigo-600 hover:underline">view</summary>
                      <pre className="mt-1 bg-gray-50 border border-gray-200 rounded p-2 overflow-auto text-[11px] leading-snug">
                        {JSON.stringify(ev.details, null, 2)}
                      </pre>
                    </details>
                  ) : <span className="text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-500">Page {page} of {totalPages}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            data-testid="audit-prev-page"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            data-testid="audit-next-page"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLog;
